import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAppSelector } from '@/hooks';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import { tenantNavItems as navItems, platformNavItems as platformAdminItems } from '@/components/Layout/navItems';


interface SidebarProps {
  /** Called when a nav item is tapped or the close button is pressed (mobile). */
  onClose?: () => void;
}

/** Multiply each RGB channel by `factor` to produce a darker shade (mix over black). */
function darkenHex(hex: string, factor: number): string {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
  const toHex = (v: number) => Math.min(255, Math.max(0, v)).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function useDarkMode(): boolean {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    obs.observe(document.documentElement, { attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const roles = useAppSelector((s) => s.sessionState.roles);
  const primaryColor = useAppSelector((s) => s.brand.primaryColor) ?? '#4E38B5';
  const isPlatformRole =
    roles.includes('platform_admin') || roles.includes('platform_support');
  const isDark = useDarkMode();

  // Derive sidebar base color: darken the primary so the gradient stays deep regardless
  // of how vivid the brand color is. Dark mode uses a heavier shade (near-black purple).
  const sidebarPrimary = isDark
    ? darkenHex(primaryColor, 0.15)
    : darkenHex(primaryColor, 0.42);

  // Nav link classes use semantic tokens — no JS dark check needed.
  // Light mode:  brand-colored active state on light bg
  // Dark mode:   white-tint active state on dark bg  (CSS vars flip automatically)
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
      isActive
        ? 'bg-brand-dark/10 text-brand-dark dark:bg-white/15 dark:text-white'
        : 'text-ui-text2 hover:bg-ui-hover hover:text-ui-text dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white'
    );

  const labelClass =
    'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium cursor-default select-none text-ui-text3 dark:text-white/40';

  // The sidebar always shows the SmartContractor product brand (default logo + wordmark from
  // LeftSidePanel) for every user, tenant or platform. Tenant company name + logo are shown in the
  // top bar instead — not here. Omitting logoIcon/appName makes LeftSidePanel render its default.
  return (
    <LeftSidePanel
      width={280}
      primaryColor={sidebarPrimary}
      lightMode={false}
      footer={
        !isPlatformRole ? (
          <NavLink to="/settings/tenant" className={linkClass} onClick={onClose}>
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </NavLink>
        ) : null
      }
    >
      {isPlatformRole ? (
        <div className="space-y-1">
          {platformAdminItems.map((item) => (
            <NavLink key={item.path} to={item.path} className={linkClass} onClick={onClose}>
              {item.label}
            </NavLink>
          ))}
        </div>
      ) : (
        <div className="space-y-1">
          {navItems.map((item) =>
            item.path ? (
              <NavLink key={item.path} to={item.path} className={linkClass} onClick={onClose}>
                {item.label}
              </NavLink>
            ) : (
              <div key={item.label} className={labelClass} aria-disabled="true">
                {item.label}
              </div>
            )
          )}
        </div>
      )}
    </LeftSidePanel>
  );
};

export default Sidebar;
